import { useCreateAssessmentMutation } from '@/app/redux/endpoints/assessments';
import { useBulkAssignStudentAssessmentsMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  setStudentCreationCallback,
  setStudentCreationModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import scopeAndSequence from '@/common/data/scopeAndSequence.json';
import { ASSESSMENT_VERSION } from '@/common/types/migrations/assessment';
import { ControllerType } from '@/common/types/student';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { showAssessmentTip } from '@/common/utils/tips';
import { LearnerMultiSelect } from '@/dashboard/MultiSelect';
import { MouseLeftClickIcon } from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { chunk } from 'lodash';
import { AlertCircle, Keyboard } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import styled from 'styled-components';

interface CreateAssessmentDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  initialStudents?: string[];
}

export default function CreateAssessmentDialogue({
  isOpen,
  onClose,
  initialStudents,
}: CreateAssessmentDialogueProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { students } = useStudents();
  const [createAssessment] = useCreateAssessmentMutation();
  const [bulkAssignStudentAssessments] =
    useBulkAssignStudentAssessmentsMutation();

  const userId = useAppSelector(selectUserId);

  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(
    new Set(initialStudents),
  );
  const [selectedPhase, setSelectedPhase] = useState(0);
  const [placeAtAssessedLevel, setPlaceAtAssessedLevel] = useState(false);
  const [selectedControl, setSelectedControl] = useState<ControllerType>(
    ControllerType.KEYBOARD,
  );
  const [doScreener, setDoScreener] = useState(true);
  const [error, setError] = useState<string | JSX.Element>('');
  const [isLoading, setIsLoading] = useState(false);
  const [temporarilyHidden, setTemporarilyHidden] = useState(false);

  const handleAddItem = () => {
    setTemporarilyHidden(true);
    setStudentCreationCallback((studentId) => {
      setTemporarilyHidden(false);
      if (studentId) {
        setSelectedStudents((prev) => new Set([...prev, studentId]));
      }
    });
    dispatch(setStudentCreationModalOpen(true));
  };

  useEffect(() => {
    setSelectedStudents(new Set(initialStudents ?? []));
  }, [initialStudents]);

  function handleSelect(studentId: string) {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  function handleFilter(searchTerm: string) {
    return (students || []).filter((student) => {
      const key = `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });
  }

  async function handleCreateAssessment() {
    if (isLoading) return;
    setError('');

    if (selectedStudents.size === 0) {
      setError('Please select at least one learner');
      return;
    }

    if (!userId || !currentOrganisationId) {
      console.error('Missing user ID or org ID');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    setIsLoading(true);

    try {
      const now = Date.now();
      const newAssessment = await createAssessment({
        version: ASSESSMENT_VERSION,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        organisationId: currentOrganisationId,
        phase: selectedPhase,
        placeAtAssessedLevel,
        control: selectedControl,
        doScreener,
        students: Object.fromEntries(
          [...selectedStudents].map((id) => [
            id,
            {
              id,
              startedAt: null,
              completedAt: null,
              placementLevel: null,
              phase: selectedPhase,
              placeAtAssessedLevel,
              control: selectedControl,
              doScreener,
            },
          ]),
        ),
        studentIds: Array.from(selectedStudents),
      }).unwrap();
      const newAssessmentId = newAssessment.id;

      // Close dialog and navigate before batched assignment
      const allStudentIds = Array.from(selectedStudents);
      onClose();
      navigate(`/dashboard/assessments/${newAssessmentId}`);
      showAssessmentTip();

      // Reset all state
      setSelectedStudents(new Set());
      setSelectedPhase(0);
      setPlaceAtAssessedLevel(false);
      setSelectedControl(ControllerType.KEYBOARD);
      setIsLoading(false);

      // Add assessmentId to selected students in batches
      const chunks = chunk(allStudentIds, 50);
      let assigned = 0;

      const toastId = toast.loading('Assigning assessment...', {
        description: "Please don't refresh or leave the page",
      });

      try {
        for (const idChunk of chunks) {
          await bulkAssignStudentAssessments({
            studentIds: idChunk,
            assessmentId: newAssessmentId,
            organisationId: currentOrganisationId,
          });
          assigned += idChunk.length;
          toast.loading(
            `Assigning assessment (${assigned}/${allStudentIds.length})`,
            { id: toastId },
          );
        }

        trackMixpanelEvent('Create Assessment', {
          students: allStudentIds,
          phase: selectedPhase,
          control: selectedControl,
          placeAtAssessedLevel,
        });
        toast.success('Assessment created', { id: toastId, description: '' });
      } catch (assignError) {
        console.error('Error assigning assessment to students', assignError);
        if (assigned > 0) {
          toast.error('Failed to assign assessment', {
            id: toastId,
            description: `Assigned ${assigned} of ${allStudentIds.length} learners. Please retry.`,
          });
        } else {
          toast.error('Failed to assign assessment', {
            id: toastId,
            description: UNKNOWN_ERROR_MESSAGE,
          });
        }
      }
    } catch (error) {
      console.error('Error creating assessment', error);
      setError(UNKNOWN_ERROR_MESSAGE);
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={isOpen && !temporarilyHidden} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Create Assessment</DialogTitle>
          <DialogDescription>
            Select options below to create a assessment for the selected
            learner(s).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle size={16} className="ml-2" />
              <span>{error}</span>
            </Alert>
          )}

          <InputContainer>
            <Label htmlFor="learner-select">Learner(s)</Label>
            {/* If initial student IDs were supplied, just render them as chips */}
            {initialStudents ? (
              <div className="flex items-center gap-1 flex-wrap">
                {/* Truncate to first 10 students */}
                {Array.from(selectedStudents)
                  .slice(0, 10)
                  .map((studentId) => (
                    <StudentChip key={studentId} studentId={studentId} />
                  ))}
                {/* Chip w/ tooltip to list remaining students */}
                {selectedStudents.size > 10 && (
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-2 py-1 px-2 bg-accent rounded-md text-sm text-muted-foreground hover:text-foreground cursor-pointer">
                      + {selectedStudents.size - 10} more
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <ul className="list-disc list-inside text-sm">
                        {Array.from(selectedStudents)
                          .slice(10)
                          .map((studentId) => (
                            <StudentChip
                              key={studentId}
                              studentId={studentId}
                              forTooltip
                            />
                          ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            ) : (
              <LearnerMultiSelect
                items={students || []}
                selectedIds={Array.from(selectedStudents)}
                onSelect={handleSelect}
                onFilter={handleFilter}
                getItemDisabled={(s) => !!s.assessmentId}
                disabled={isLoading}
                getDisabledReason={(s) => {
                  if (s.assessmentId) {
                    return `${s.studentFirstName} ${s.studentSurname} already has an active assessment`;
                  }
                  return '';
                }}
                onAddItem={handleAddItem}
              />
            )}
          </InputContainer>

          <InputContainer>
            <Label htmlFor="content">Assessment Content</Label>
            <Select
              value={selectedPhase.toString()}
              onValueChange={(value) => {
                const newPhase = Number(value);
                setSelectedPhase(newPhase);
                setDoScreener(newPhase === 0);
              }}
              disabled={isLoading}
            >
              <SelectTrigger id="content">
                {selectedPhase === 0 ? (
                  <span className="font-medium">Screener (Phases 1-3)</span>
                ) : (
                  <span className="font-medium">Phase {selectedPhase}</span>
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={'0'} className="max-w-md">
                  <span className="font-medium">Screener (Phases 1-3)</span>
                  <br />
                  <span className="text-xs text-muted-foreground">
                    Start with a screener to assess all of Phases 1-3 at once.
                  </span>
                </SelectItem>
                {scopeAndSequence.phases.map((phase) => (
                  <SelectItem
                    key={phase.phase}
                    value={phase.phase.toString()}
                    className="max-w-md"
                    disabled={phase.phase === 5}
                  >
                    <span className="font-medium">Phase {phase.phase}</span>
                    {phase.phase === 5 && (
                      <span className="text-xs ml-1">(Coming soon)</span>
                    )}
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {phase.description}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ">
              Select the phase of the Scope & Sequence to assess.
            </span>
          </InputContainer>

          <InputContainer>
            <Label htmlFor="control">Control Type</Label>
            <Select
              value={selectedControl}
              onValueChange={(value) =>
                setSelectedControl(value as ControllerType)
              }
              disabled={isLoading}
            >
              <SelectTrigger id="control">
                <SelectValue placeholder="Select control type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ControllerType.KEYBOARD}>
                  <span className="flex items-center gap-2">
                    <Keyboard size={16} />
                    Typing
                  </span>
                </SelectItem>
                <SelectItem value={ControllerType.CLICK}>
                  <span className="flex items-center gap-2">
                    <MouseLeftClickIcon size={16} />
                    Click to Place
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">
              Select the control type to use for the assessment{' '}
              <strong>(we advise Click to Place for tablets)</strong>.
            </span>
          </InputContainer>

          <InputContainer>
            <CheckboxContainer>
              <Checkbox
                id="place-at-assessed-level"
                checked={placeAtAssessedLevel}
                onCheckedChange={(checked) =>
                  setPlaceAtAssessedLevel(
                    checked === 'indeterminate' ? false : checked,
                  )
                }
                disabled={isLoading}
              />
              <Label htmlFor="place-at-assessed-level">
                Place learner(s) at their assessed level
              </Label>
            </CheckboxContainer>
            <span className="text-xs text-muted-foreground">
              Automatically place learner(s) at their assessed level after the
              test.
            </span>
          </InputContainer>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>

          <Button onClick={handleCreateAssessment} loading={isLoading}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface StudentChipProps {
  studentId: string;
  forTooltip?: boolean;
}
function StudentChip({ studentId, forTooltip }: StudentChipProps) {
  const { studentsById } = useStudents();
  const student = studentsById.get(studentId);

  if (!student) return null;
  if (forTooltip) {
    return (
      <li>
        {student.studentFirstName} {student.studentSurname}
      </li>
    );
  }
  return (
    <div className="flex items-center gap-2 py-1 px-2 bg-accent rounded-md text-sm">
      <InitialsCircle
        userName={`${student.studentFirstName} ${student.studentSurname}`}
        size={18}
      />
      {student.studentFirstName} {student.studentSurname}
    </div>
  );
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
